import {isEmpty, notEmpty, notNil, union} from '@megaphonenpm/mputils/src/utils.js';
import {STATE_SCENE, STATE_SCENE_RESULT, STATE_SCENE_SCOREBOARD} from "./constants.js";
import {get, isNil} from 'lodash-es';
import {warn} from '@megaphonenpm/mputils/src/logfunctions.js';

export class ThemeService {

  constructor (appState, dataStore) {
    this.appState = appState;
    this.dataStore = dataStore;
  }

  getTheme (themeID) {
    let themes = get(this.dataStore, 'themes');
    let theme = notNil(themes) ? themes.find(theme => theme.id === themeID) : null;
    return theme;
  }

  /**
   * Returns the current active ThemeSet, cascading from scene to playlist to deployment setting.
   * During scenes, the ThemeSet will be the current scene's ThemeSet, or if not specified, the
   * playlist's ThemeSet, or if not specified, the deployment's default themeset. In non-scene states
   * such as Main Scoreboard, the ThemeSet will be the playlist's current ThemeSet, or if not
   * specified, the deployment's default themeset.
   */
  getCurrentThemeSet () {
    let themeSets = get(this.dataStore, 'themeSets');
    let currentThemeSetID = this.getCurrentThemeSetID();

    let currentThemeSet = themeSets.find(themeSet => themeSet.id === currentThemeSetID);
    if (isNil(currentThemeSet)) {
      warn(`NO THEMESET FOUND FOR CURRENT STATE. REQUESTED THEMESET ID: ${currentThemeSetID}. `
        +'CONTENT CAN NOT BE RENDERED. CHECK PROJECT SETTINGS TO ENSURE REQUESTED THEMESET IS AVAILABLE.');
    }
    return currentThemeSet;
  }

  /**
   * Returns the ID of the current active ThemeSet.
   */
  getCurrentThemeSetID () {
    let currentThemeSetID = null;

    // Attempt to find a themeset id on the current scene (if any) or playlist...
    if (this.appState.state === STATE_SCENE
      || this.appState.state === STATE_SCENE_RESULT
      || this.appState.state === STATE_SCENE_SCOREBOARD) {
      // SCENE STATES:
      //   Search for theme on scene object, then cascade to original playlist theme
      currentThemeSetID = get(this.appState.scene, 'presentation.themeset_id');

      // Special Case to Handle Update Playlist:
      // If the scene, itself, does not specify a theme, then check whether the scene's original playlist
      // defined a default theme. MRTS delivers the scene's original playlist via the s17 message (see
      // MRTSConnection). This special case is required because after an Update Playlist has occurred,
      // the current scene's original playlist theme might not match the new current playlist's theme, so
      // MRTS must explicitly supply the original playlist theme for the scene.
      if (isEmpty(currentThemeSetID)) {
        currentThemeSetID = get(this.appState, 'themeset_id_from_current_scenes_playlist');
      }
      if (isEmpty(currentThemeSetID)) {
        // When MegaScreen is running in "manual mode," where the current scene is not assigned by MRTS,
        // then themeset_id_from_current_scenes_playlist will be empty, so use the current playlist's
        // theme instead. For more information on MegaScreen's "manual mode", see AppStateSource.MANUAL.
        currentThemeSetID = get(this.dataStore, 'playlist.defaults.presentation.themeset_id');
      }
    } else {
      // NON-SCENE STATES:
      //   Search for theme on current playlist
      currentThemeSetID = get(this.dataStore, 'playlist.defaults.presentation.themeset_id');
    }

    if (isEmpty(currentThemeSetID)) {
      // No scene or playlist themeset was found, so use the project default themeset
      currentThemeSetID = get(this.dataStore, 'deploymentSettings.defaults.presentation.themeset_id');
    }

    return currentThemeSetID;
  }

  /**
   * Returns the "cascading ThemeSet" ID for the specified scene, which is the ID of the ThemeSet
   * that would be used for the specified scene in the project's current active playlist if the
   * scene were to become the run's current scene. The "cascading ThemeSet" ID is determined as
   * follows:
   *
   *   1) If the scene specifies a ThemeSet ID, then return that ID
   *   2) Else return the current active playlist's specified ThemeSet ID, if any
   *   3) Else return the project's default ThemeSet ID
   */
  getCascadingThemeSetIDForScene (scene) {
    let cascadingThemeSetID = null;
    // Retrieve the scene's themeset
    cascadingThemeSetID = get(scene, 'presentation.themeset_id');

    if (isEmpty(cascadingThemeSetID)) {
      // No scene or scene themeset was found, so use the playlist's themeset
      cascadingThemeSetID = get(this.dataStore, 'playlist.defaults.presentation.themeset_id');
    }

    if (isEmpty(cascadingThemeSetID)) {
      // No scene or playlist themeset was found, so use the project default themeset
      cascadingThemeSetID = get(this.dataStore, 'deploymentSettings.defaults.presentation.themeset_id');
    }

    return cascadingThemeSetID;
  }

  /**
   * Returns the "cascading ThemeSet" for the specified scene. For details, see
   * getCascadingThemeSetIDForScene().
   */
  getCascadingThemeSetForScene (scene) {
    let themeSets = get(this.dataStore, 'themeSets');
    return isEmpty(themeSets) ? null : themeSets.find(themeSet => themeSet.id === this.getCascadingThemeSetIDForScene(scene));
  }

  getCurrentScreenThemeID () {
    let themeset = this.getCurrentThemeSet();
    let sceneThemeID = notNil(themeset) ? themeset.screen_theme_id : null;
    return sceneThemeID;
  }

  getCurrentScreenTheme () {
    return this.getTheme(this.getCurrentScreenThemeID());
  }

  /**
   * Returns the theme prefix that is automatically prepended to all styles that are set in
   * Google Web Designer for a theme. The prefix prevents styles in one GWD theme from conflicting
   * with other GWD themes.
   *
   * Example
   * // Original GWD CSS rule:
   * .headline_txt {
   *   font-size: 64px;
   * }
   *
   * // CSS rule after prefix has been added:
   * .applicationRoot.current_screen_theme_fieldname_Network.gwd .headline_txt {
   *   font-size: 64px;
   * }
   *
   * The .gwd CSS class is included in order to give the GWD style a higher specificity than plain
   * styles set on .applicationRoot (e.g., in main.css). This guarantees that GWD styles will override
   * default styles.
   *
   * @param themeID
   * @returns {string}
   */
  getCSSPrefixForTheme (themeID) {
    return `.applicationRoot.current_screen_theme_fieldname_${this.getThemeFieldName(themeID)}.gwd`;
  }

  getThemeFieldName (themeID) {
    return get(this.getTheme(themeID), 'field_name', '');
  }

  /**
   * Returns a list of the ThemeSets required to rencer the the current playlist (including failover
   * to project settings if no ThemeSets are specified by the playlist).
   */
  getIDsOfRequiredThemeSets () {
    // Get array of all scene.presentation.themeset_id values
    let sceneThemeSetIDs = get(this.dataStore.playlist, 'scenes', []).map(scene => scene.presentation.themeset_id);
    // Remove duplicates and empty values from sceneThemeSetIDs
    let uniqueSceneThemeSetIDs = new Set(sceneThemeSetIDs.filter(el => notEmpty(el)));
    let playlistThemeSetID = get(this.dataStore.playlist, 'defaults.presentation.themeset_id', null);
    let projectThemeSetID = get(this.dataStore.deploymentSettings, 'defaults.presentation.themeset_id', null);
    // If the playlist specifies a ThemeSet ID, then the project ThemeSet is not required; otherwise,
    // the project's default ThemeSet is required.
    let fallbackThemeSetID = notEmpty(playlistThemeSetID) ? [playlistThemeSetID] : (notEmpty(projectThemeSetID) ? [projectThemeSetID] : []);
    let idsOfRequiredThemeSets = union(uniqueSceneThemeSetIDs, fallbackThemeSetID);
    return idsOfRequiredThemeSets;
  }

  /**
   * Returns a list of IDs of ThemeSets required by the current playlist but missing from the
   * DataStore.
   */
  getIDsOfMissingThemeSets () {
    let idsOfRequiredThemeSets = this.getIDsOfRequiredThemeSets();
    let idsOfLocalMissingThemeSets = [];

    idsOfRequiredThemeSets.forEach(themesetID => {
      if (!this.dataStore.themeSets.some(themeSet => themeSet.id === themesetID)) {
        idsOfLocalMissingThemeSets.push(themesetID);
      }
    });
    return idsOfLocalMissingThemeSets;
  }
}
